import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LogOut,
  LogIn,
  UserPlus,
  Bell,
  UserIcon,
  Home,
  TrendingUp,
  Users,
  PenTool,
  Shield,
  User,
  X,
  Menu,
} from "lucide-react";
import { notificationService, UserNotification, theEditionService } from "../services/api";
import MobileSidebar from "./MobileSidebar";

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [editionNewCount, setEditionNewCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Notification state
  const [unreadCount, setUnreadCount] = useState(0);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Load notification count for logged in users
  useEffect(() => {
    if (user) {
      loadNotificationCount();
      // Set up periodic refresh
      const interval = setInterval(loadNotificationCount, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    } else {
      setUnreadCount(0);
    }
  }, [user]);

  const loadNotificationCount = async () => {
    try {
      const response = await notificationService.getNotifications(0, 1, true); // Just get count of unread
      setUnreadCount(response.unread_count);
    } catch (error) {
      // Silently fail to avoid spamming user with errors
      console.error("Failed to load notification count:", error);
    }
  };

  const toggleDropdown = async () => {
    if (!dropdownOpen) {
      try {
        const response = await notificationService.getNotifications(0, 5); // últimas 5
        setNotifications(response.notifications);
      } catch (error) {
        console.error(error);
      }
    }
    setDropdownOpen(!dropdownOpen);
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      loadEditionCount();
      const interval = setInterval(loadEditionCount, 60000); // cada 1 minuto
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadEditionCount = async () => {
    try {
      const data = await theEditionService.getEdition();
      setEditionNewCount(data.new_articles_count);
    } catch (error) {
      console.error("Failed to load The Edition count:", error);
    }
  };

  return (
    <>
      <header className="HEADER bg-white border-b border-newspaper-200 my-4">
        {/* Top meta bar */}
        <div
          className="w-full flex md:flex-row flex-col items-center md:items-center md:justify-between gap-2"
          style={{
            alignSelf: "stretch",
            background: "rgba(0, 0, 0, 0)",
            display: "inline-flex",
          }}
        >
          <div
            className="text-center md:text-left"
            // className="w-full"
            style={{
              width: "258px",
              height: "16px",
              color: "#737373",
              fontSize: "16px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              lineHeight: "16px",
              wordWrap: "break-word",
            }}
          >
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div
            className="flex flex-row items-center gap-2 mt-2 md:mt-0 justify-center md:justify-end"
            style={{
              width: "228px",
              background: "rgba(0, 0, 0, 0)",
              alignItems: "center",
              gap: "10px",
              display: "flex",
            }}
          >
            <Link
              to="/about"
              style={{
                color: "#737373",
                fontSize: "13px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                lineHeight: "16px",
                letterSpacing: 0.6,
                wordWrap: "break-word",
                paddingLeft: "12px",
                paddingRight: "12px",
                paddingTop: "10px",
                paddingBottom: "10px",
                borderRadius: "12px",
                cursor: "pointer",
                textAlign: "center",
                textDecoration: "none",
              }}
            >
              ABOUT
            </Link>
            <Link
              to="/contact"
              style={{
                color: "#737373",
                fontSize: "13px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                lineHeight: "16px",
                letterSpacing: 0.6,
                wordWrap: "break-word",
                paddingLeft: "12px",
                paddingRight: "12px",
                paddingTop: "10px",
                paddingBottom: "10px",
                borderRadius: "12px",
                cursor: "pointer",
                textAlign: "center",
                textDecoration: "none",
              }}
            >
              CONTACT
            </Link>
          </div>
        </div>

        {/* Brand row */}
        <div
          className="beat-container w-full my-5 px-4 sm:px-8 md:px-12 xl:px-[248px]"
          style={{
            alignSelf: "stretch",
            paddingTop: "32px",
            paddingBottom: "32px",
            background: "rgba(0, 0, 0, 0)",
            justifyContent: "center",
            alignItems: "center",
            gap: "20px",
            display: "inline-flex",
          }}
        >
          {/* <div className="flex items-center justify-center"> */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/LogoBeatPost.png"
              alt="Beatpost"
              // className="h-16 w-16"
              style={{ width: "93px", height: "85px" }}
            />
            <div
              className="text-[40px] leading-[44px] md:text-[56px] md:leading-[60px] xl:text-[72px] xl:leading-[72px]"
              style={{
                textAlign: "center",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                color: "#171717",
                fontFamily: "Playfair Display",
                fontWeight: "600",
                wordWrap: "break-word",
              }}
            >
              Beatpost
            </div>
          </Link>
          {/* </div> */}
        </div>
        {/* Nav row with centered nav and right user menu */}
        <div className="border-t border-newspaper-200 hidden xl:block">
          <div className="beat-container relative flex items-center justify-between border-y border-[#D3D3D3]">
            <nav
              className="hidden xl:flex"
              style={{
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  background: "rgba(0, 0, 0, 0)",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  display: "inline-flex",
                }}
              >
                <Link
                  to="/"
                  className={`beat-nav-link px-6 py-4 ${isActive("/") ? "active text-pink-700" : "text-[#737373]"}`}
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "inline-flex",
                    fontSize: "16px",
                    fontFamily: "Playfair Display",
                    fontWeight: "400",
                    lineHeight: "20px",
                    letterSpacing: 0.7,
                    wordWrap: "break-word",
                    textAlign: "center",
                  }}
                >
                  FrontPage
                </Link>
              </div>

              {user && (
                <div
                  style={{
                    background: "rgba(0, 0, 0, 0)",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    display: "inline-flex",
                  }}
                >
                  <Link
                    to="/the-edition"
                    className={`beat-nav-link px-6 py-4 ${
                      isActive("/the-edition") ? "active text-pink-700" : "text-[#737373]"
                    }`}
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      display: "inline-flex",
                      fontSize: "16px",
                      fontFamily: "Playfair Display",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: 0.7,
                      wordWrap: "break-word",
                      textAlign: "center",
                      position: "relative",
                    }}
                  >
                    The Edition
                    {editionNewCount > 0 && (
                      <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {editionNewCount > 9 ? "9+" : editionNewCount}
                      </span>
                    )}
                  </Link>
                </div>
              )}

              <div
                style={{
                  background: "rgba(0, 0, 0, 0)",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  display: "inline-flex",
                }}
              >
                <Link
                  to="/thepulse"
                  className={`beat-nav-link px-6 py-4 ${
                    isActive("/thepulse") ? "active text-pink-700" : "text-[#737373]"
                  }`}
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "inline-flex",
                    fontSize: "16px",
                    fontFamily: "Playfair Display",
                    fontWeight: "400",
                    lineHeight: "20px",
                    letterSpacing: 0.7,
                    wordWrap: "break-word",
                    textAlign: "center",
                  }}
                >
                  The Pulse
                </Link>
              </div>

              <div
                style={{
                  background: "rgba(0, 0, 0, 0)",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  display: "inline-flex",
                }}
              >
                <Link
                  to="/voices"
                  className={`beat-nav-link px-6 py-4 ${
                    isActive("/voices") ? "active text-pink-700" : "text-[#737373]"
                  }`}
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "inline-flex",
                    fontSize: "16px",
                    fontFamily: "Playfair Display",
                    fontWeight: "400",
                    lineHeight: "20px",
                    letterSpacing: 0.7,
                    wordWrap: "break-word",
                    textAlign: "center",
                  }}
                >
                  Voices
                </Link>
              </div>

              {user && (
                <div
                  style={{
                    background: "rgba(0, 0, 0, 0)",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    display: "inline-flex",
                  }}
                >
                  <Link
                    to="/write"
                    className={`beat-nav-link px-6 py-4 ${
                      isActive("/write") ? "active text-pink-700" : "text-[#737373]"
                    }`}
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      display: "inline-flex",
                      fontSize: "16px",
                      fontFamily: "Playfair Display",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: 0.7,
                      wordWrap: "break-word",
                      textAlign: "center",
                    }}
                  >
                    Write
                  </Link>
                </div>
              )}
              {user && user.level === "admin" && (
                <div
                  style={{
                    background: "rgba(0, 0, 0, 0)",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    display: "inline-flex",
                  }}
                >
                  <Link
                    to="/admin"
                    className={`beat-nav-link px-6 py-4 ${
                      isActive("/admin") ? "active text-pink-700" : "text-[#737373]"
                    }`}
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      display: "inline-flex",
                      fontSize: "16px",
                      fontFamily: "Playfair Display",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: 0.7,
                      wordWrap: "break-word",
                      textAlign: "center",
                    }}
                  >
                    Admin
                  </Link>
                </div>
              )}
            </nav>
            {/* User Menu aligned right */}
            <div className="absolute right-0 hidden xl:flex items-center space-x-2 xl:space-x-4">
              {user ? (
                <>
                  {/* Notifications Bell */}
                  {/* Notifications Bell with Dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    <button onClick={toggleDropdown} className="beat-nav-link relative flex items-center space-x-1">
                      <Bell className="w-4 h-4" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </button>

                    {dropdownOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white shadow-lg rounded-lg overflow-hidden z-50">
                        {/* Header del dropdown con botón “Mark all as read” */}
                        {notifications.length > 0 && (
                          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-200">
                            <span className="font-semibold text-sm">Notifications</span>
                            <button
                              onClick={async () => {
                                try {
                                  await notificationService.markAllAsRead();
                                  setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
                                  setUnreadCount(0);
                                } catch (error) {
                                  console.error("Error marking all notifications as read:", error);
                                }
                              }}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Mark all as read
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await notificationService.clearAll();
                                  setNotifications([]);
                                  setUnreadCount(0);
                                } catch (error) {
                                  console.error("Error clearing notifications:", error);
                                }
                              }}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Clear all
                            </button>
                          </div>
                        )}

                        {notifications.length === 0 ? (
                          <div className="p-2 text-gray-500 text-sm">No new notifications</div>
                        ) : (
                          notifications.map((n) => (
                            <div
                              key={n.id}
                              className={`p-2 text-sm cursor-pointer ${
                                n.read ? "bg-gray-100" : "bg-white hover:bg-gray-50"
                              }`}
                              onClick={async () => {
                                if (!n.read) {
                                  await notificationService.markAsRead(n.id);
                                  setUnreadCount((prev) => Math.max(prev - 1, 0));
                                  n.read = true;
                                }
                                window.location.href = "/notifications";
                              }}
                            >
                              <div className="font-semibold">{n.title || "Notification"}</div>
                              <div className="text-gray-600 text-xs">{n.message}</div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  <Link to="/profile" className="w-full flex justify-end items-center space-x-1 gap-4">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.username} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <UserIcon className="w-7 h-7 text-newspaper-600" />
                    )}
                    <div
                      className="hidden lg:inline"
                      style={{
                        color: "#737373",
                        fontSize: "14px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "400",
                        lineHeight: "16px",
                        letterSpacing: 0.6,
                        wordWrap: "break-word",
                      }}
                    >
                      {user.username}
                    </div>
                    <div className="beat-mojo rounded-lg">{Math.round(user.mojo)} Mojo</div>
                  </Link>

                  <button
                    onClick={logout}
                    className="beat-nav-link flex items-center space-x-1 text-red-600 rounded-lg"
                  >
                    <LogOut className="w-4 h-4" />
                    {/* <span className="hidden md:inline">Logout</span> */}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center gap-[10px] rounded-[49px] pl-[12px] md:pl-[16px] pr-[12px] md:pr-[16px] pt-[8px] md:pt-[9px] pb-[8px] md:pb-[9px]"
                    style={{
                      outline: "1px black solid",
                      outlineOffset: "-1px",
                      textDecoration: "none",
                    }}
                  >
                    <LogIn className="w-4 h-4" style={{ color: "black" }} />
                    <span
                      className="text-[13px] md:text-[14px]"
                      style={{
                        color: "black",
                        fontFamily: "Abhaya Libre ExtraBold",
                        fontWeight: "800",
                        wordWrap: "break-word",
                      }}
                    >
                      Login
                    </span>
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-[10px] rounded-[49px] pl-[12px] md:pl-[16px] pr-[12px] md:pr-[16px] pt-[8px] md:pt-[9px] pb-[8px] md:pb-[9px]"
                    style={{
                      background: "#262626",
                      textDecoration: "none",
                    }}
                  >
                    <UserPlus className="w-4 h-4" style={{ color: "white" }} />
                    <span
                      className="text-[13px] md:text-[14px]"
                      style={{
                        color: "white",
                        fontFamily: "Abhaya Libre ExtraBold",
                        fontWeight: "800",
                        wordWrap: "break-word",
                      }}
                    >
                      Register
                    </span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="xl:hidden bg-white border-b border-newspaper-300">
        <div className="beat-container">
          <div className="flex items-center justify-start sm:justify-around py-3">
            <button
              onClick={() => setShowMobileSidebar(true)}
              className="beat-nav-link flex flex-col items-center space-y-1 sm:hidden ml-4 sm:ml-0"
            >
              <Menu className="w-5 h-5" />
              <span className="text-xs">Menu</span>
            </button>
            <div className={`${showMobileSidebar ? "hidden" : "hidden sm:flex"} items-center justify-around gap-3`}>
              <Link
                to="/"
                className={`beat-nav-link flex flex-col items-center space-y-1 ${isActive("/") ? "active" : ""}`}
              >
                <Home className="w-5 h-5" />
                <span className="text-xs">FrontPage</span>
              </Link>

              {user && (
                <Link to="/the-edition" className="beat-nav-link flex flex-col items-center space-y-1 relative">
                  <PenTool className="w-5 h-5" />
                  {editionNewCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {editionNewCount > 9 ? "9+" : editionNewCount}
                    </span>
                  )}
                  <span className="text-xs">The Edition</span>
                </Link>
              )}

              <Link
                to="/thepulse"
                className={`beat-nav-link flex flex-col items-center space-y-1 ${
                  isActive("/thepulse") ? "active" : ""
                }`}
              >
                <TrendingUp className="w-5 h-5" />
                <span className="text-xs">The Pulse</span>
              </Link>

              <Link
                to="/voices"
                className={`beat-nav-link flex flex-col items-center space-y-1 ${isActive("/voices") ? "active" : ""}`}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs">Voices</span>
              </Link>

              {user && (
                <Link
                  to="/write"
                  className={`beat-nav-link flex flex-col items-center space-y-1 ${isActive("/write") ? "active" : ""}`}
                >
                  <PenTool className="w-5 h-5" />
                  <span className="text-xs">Write</span>
                </Link>
              )}

              {user && user.level === "admin" && (
                <Link
                  to="/admin"
                  className={`beat-nav-link flex flex-col items-center space-y-1 ${isActive("/admin") ? "active" : ""}`}
                >
                  <Shield className="w-5 h-5" />
                  <span className="text-xs">Admin</span>
                </Link>
              )}

              {user ? (
                <>
                  <Link to="/notifications" className="beat-nav-link flex flex-col items-center space-y-1 relative">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                        {unreadCount > 9 ? "9" : unreadCount}
                      </span>
                    )}
                    <span className="text-xs">Notif</span>
                  </Link>

                  <Link to="/profile" className="beat-nav-link flex flex-col items-center space-y-1">
                    <User className="w-5 h-5" />
                    <span className="text-xs">Profile</span>
                  </Link>

                  <button
                    onClick={() => setShowLogoutConfirm(true)}
                    className="beat-nav-link flex flex-col items-center space-y-1 text-red-600"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="text-xs">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="beat-nav-link flex flex-col items-center space-y-1">
                    <LogIn className="w-5 h-5" />
                    <span className="text-xs">Login</span>
                  </Link>
                  <Link to="/register" className="beat-nav-link flex flex-col items-center space-y-1">
                    <UserPlus className="w-5 h-5" />
                    <span className="text-xs">Register</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      <MobileSidebar
        open={showMobileSidebar}
        onClose={() => setShowMobileSidebar(false)}
        isAuthenticated={!!user}
        isAdmin={!!user && user.level === "admin"}
        onLogout={logout}
      />
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-newspaper-300">
              <div
                style={{
                  color: "#171717",
                  fontSize: "18px",
                  fontFamily: "Playfair Display",
                  fontWeight: "600",
                  wordWrap: "break-word",
                }}
              >
                Confirm Logout
              </div>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="text-newspaper-500 hover:text-newspaper-700 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="text-newspaper-700 mb-4">Are you sure you want to log out?</div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="px-4 py-2 rounded-lg border border-newspaper-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    logout();
                    setShowLogoutConfirm(false);
                  }}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
